<template>
  <section-page
    title="Task Templates"
    description="These tasks will be added all at once by just selecting the task list throughout Forefront."
  >
    <template v-slot:right>
      <button-outlined icon-left="plus" @click="addTemplate">
        Add Template
      </button-outlined>
    </template>

    <app-loading-section
      :count="listTemplates.length"
      :loading="isLoading"
      heading="Add task templates!"
    >
      <table-default
        :table-data="listTemplates"
        default-sort="label"
      >
        <b-table-column label="Task" field="label" sortable v-slot="props">
          {{ props.row.label }}
        </b-table-column>

        <b-table-column label="User Role" field="user_role" v-slot="props">
          {{ getRoleLabel(props.row.user_role) }}
        </b-table-column>

        <b-table-column label="Assigned To" field="assigned_to_id" v-slot="props">
          {{ getAssignedUser(props.row) }}
        </b-table-column>

        <b-table-column label="Description" field="description" width="280" v-slot="props">
          {{ utils.truncateString(props.row.description, 55) }}
        </b-table-column>

        <b-table-column cell-class="action_cell" width="100" v-slot="props">
          <button-link @click="editTemplate(props.row)">Manage</button-link>
        </b-table-column>
      </table-default>
    </app-loading-section>

    <modal-container>
      <modal-default
        ref="addTemplateModal"
        title="Add Task Template"
      >
        <app-loading-section :loading="loadingExistingTemplates">
          <b-tabs class="mb-0" v-model="templateModalTab">
            <b-tab-item label="Create New Template" value="new">
              <task-template-form
                class="status-add-checklist-item"
                :show-buttons="true"
                :task-list-id="taskListId"
                @created="addTaskTemplate"
              ></task-template-form>
            </b-tab-item>

            <b-tab-item label="Existing Templates" value="existing">
              <div class="task-templates">
                <template v-if="existingTemplates.length !== 0">
                  <button-link
                    class="task-templates--item"
                    size="is-normal"
                    v-for="template in existingTemplates"
                    :key="template.id"
                    @click="attachTemplate(template)"
                  >{{ template.label }} - {{ getAssignedUser(template) }}</button-link>
                </template>
                <template v-else>
                  <p class="task-templates--empty-message">No task templates available</p>
                </template>
              </div>
            </b-tab-item>
          </b-tabs>
        </app-loading-section>
      </modal-default>

      <modal-crud-form
        ref="taskTemplateModal"
        title="Task Template"
        form-component="task-template-form"
        hide-delete-button
        @saved="loadListTemplates"
      >
        <template v-slot:buttons>
          <div class="mr-auto">
            <button-link
              color="is-danger"
              :loading="isDeleting"
              @click="removeTemplate"
            >
              Remove
            </button-link>
          </div>
        </template>
      </modal-crud-form>
    </modal-container>
  </section-page>
</template>

<script>
import api from "../../../api";
import {ENUMS} from "../../../services/enums";
import utils from "../../../services/utils";
import TaskTemplateForm from "./edit-status/TaskTemplateForm.vue";

export default {
  name: "TaskListTemplates",
  components: {TaskTemplateForm},
  props: {
    taskListId: {
      type: Number,
      required: true
    },
  },
  data() {
    return {
      form: {},
      isLoading: false,
      isDeleting: false,
      loadingExistingTemplates: false,
      templateModalTab: 'existing',
      listTemplates: [],
      existingTemplates: [],
    }
  },
  mounted() {
    this.loadListTemplates()
  },
  computed: {
    utils() {
      return utils
    },
    users() {
      return this.$store.getters.activeUsers
    },
  },
  methods: {
    getRoleLabel(userRole) {
      const role = Object.values(ENUMS.UserRoleEnum).find(role => role.value === userRole)
      return (role) ? role.label : '--'
    },
    getAssignedUser(template) {
      const user = this.users.find(u => u.id === template.assign_to_id)
      return (user) ? user.full_name : '--'
    },
    loadListTemplates() {
      this.isLoading = true
      api.taskLists.get(this.taskListId)
        .then((res) => {
          this.listTemplates = res.data.task_templates
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    loadExistingTemplates() {
      this.loadingExistingTemplates = true
      api.taskTemplates.all()
        .then((res) => {
          // shows only available list
          const listTemplateIds = this.listTemplates.map(t => t.id)
          this.existingTemplates = res.data.filter(template => !listTemplateIds.includes(template.id))
        })
        .finally(() => {
          this.loadingExistingTemplates = false
        })
    },
    addTemplate() {
      this.$refs.addTemplateModal.open()
      this.loadExistingTemplates()
    },
    editTemplate(template) {
      this.$refs.taskTemplateModal.open(template)
      this.form = template
    },
    addTaskTemplate(template) {
      this.listTemplates.push(template)
    },
    attachTemplate(template) {
      api.taskLists.attachTemplate(this.taskListId, template.id)
        .then((res) => {
          // adds template to template list
          this.listTemplates.push(template)
          // removes template from the available list
          const templateIndex = this.existingTemplates.findIndex(t => t.id === template.id)
          this.existingTemplates.splice(templateIndex, 1)

          this.$buefy.toast.open({
            message: "Task template was added on the list",
            type: 'is-success'
          })
        })
        .finally(() => {})
    },
    removeTemplate() {
      this.isDeleting = true
      api.taskLists.detachTemplate(this.taskListId, this.form.id)
        .then((res) => {
          this.$buefy.toast.open({
            message: "Task template was removed on the list",
            type: 'is-success'
          })

          // removes template from the list
          const templateIndex = this.listTemplates.findIndex(t => t.id === this.form.id)
          this.listTemplates.splice(templateIndex, 1)

          this.$refs.taskTemplateModal.close()
        })
        .finally(() => {
          this.isDeleting = false
        })
    }
  },
}
</script>

<style scoped lang="scss">
@import "../../../styles/variables/variables";

.task-templates {
  padding: 15px 0;

  &--item {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &--empty-message {
    font-size: 1rem;
    color: $text-light;
    line-height: 1.4;
  }
}
</style>
